// Lead Insights (Module F) — the Lead's private per-teacher picture for training/meetings. LEAD-ONLY
// route. Built from the SHARED read-model (Tier-1 work) + the Lead-private `lead_tracking` notes
// (People_Progress: last review · last observation · current focus · next action). Never team-visible;
// no quality grade, no ranking — on-time is a calm coordination stat, late = a quiet time. One-thumb.

import { api } from "../api";
import { esc } from "../dom";

type LessonStatus = "not_started" | "in_progress" | "submitted" | "approved";

interface Rollup {
  teacher_id: string;
  teacher_name: string;
  total: number;
  submitted: number;
  approved: number;
  drafts: number;
  on_time: number;
  on_time_rate: number | null;
  last_submitted_at: string | null;
}
interface TimelineRow {
  class_name: string;
  topic: string;
  week_no: number;
  status: LessonStatus;
  on_time: boolean | null;
  submitted_at: string | null;
  plan_id: string | null;
}
interface Tracking {
  teacher_id: string;
  last_review: string | null;
  last_observation: string | null;
  current_focus: string | null;
  next_action: string | null;
  updated_at: string | null;
  updated_by: string | null;
}
interface InsightsResp { term: { name: string } | null; teachers: Rollup[]; timeline?: TimelineRow[] }

const STATUS_OPTS: { key: "" | LessonStatus; label: string }[] = [
  { key: "", label: "All statuses" },
  { key: "submitted", label: "Submitted" },
  { key: "approved", label: "Approved" },
  { key: "in_progress", label: "In progress" },
  { key: "not_started", label: "Not started" },
];

function statusChip(status: LessonStatus, onTime: boolean | null): string {
  if (status === "approved") return `<span class="chip approved">Approved ✓</span>`;
  if (status === "submitted") {
    const late = onTime === false ? ` <span class="muted">(late)</span>` : "";
    return `<span class="chip submitted">Submitted</span>${late}`;
  }
  return `<span class="chip">Draft</span>`;
}

function rate(r: Rollup): string {
  if (r.on_time_rate === null) return `<span class="muted">no submissions yet</span>`;
  return `on-time ${Math.round(r.on_time_rate * 100)}%`;
}

export async function renderInsights(app: HTMLElement): Promise<void> {
  // Page-local state (closure; reset on re-entry).
  let term: { name: string } | null = null;
  let teachers: Rollup[] = [];
  let timeline: TimelineRow[] = [];
  let tracking: Tracking | null = null;
  let selectedId = "";       // "" = overview; otherwise a teacher_id (drill view)
  let statusFilter = "";     // "" = all statuses
  let err: string | null = null;
  let flash = "";

  async function load(): Promise<void> {
    const qs = new URLSearchParams();
    if (statusFilter) qs.set("status", statusFilter);
    if (selectedId) qs.set("teacher", selectedId);
    const q = qs.toString() ? `?${qs}` : "";

    if (selectedId) {
      const [ins, trk] = await Promise.all([
        api<InsightsResp>(`/api/lead/insights${q}`),
        api<{ tracking: Tracking | null }>(`/api/lead/tracking/${encodeURIComponent(selectedId)}`),
      ]);
      term = ins.term;
      teachers = ins.teachers;
      timeline = ins.timeline ?? [];
      tracking = trk.tracking;
    } else {
      const ins = await api<InsightsResp>(`/api/lead/insights${q}`);
      term = ins.term;
      teachers = ins.teachers;
      timeline = [];
      tracking = null;
    }
  }

  // ── filter bar ─────────────────────────────────────────────────────────────────
  function filterBar(): string {
    const teacherOpts =
      `<option value="">All teachers</option>` +
      teachers
        .map((t) => `<option value="${esc(t.teacher_id)}"${t.teacher_id === selectedId ? " selected" : ""}>${esc(t.teacher_name)}</option>`)
        .join("");
    const statusSel = STATUS_OPTS.map(
      (s) => `<option value="${s.key}"${s.key === statusFilter ? " selected" : ""}>${s.label}</option>`,
    ).join("");
    return `
      <div class="row-actions" style="gap:var(--space-3);margin-bottom:var(--space-4)">
        <select id="f-teacher" aria-label="Teacher" class="proj-move">${teacherOpts}</select>
        <select id="f-status" aria-label="Status" class="proj-move">${statusSel}</select>
      </div>`;
  }

  // ── overview (all teachers, rolled up) ───────────────────────────────────────────
  function overviewHtml(): string {
    if (!teachers.length) return `<p class="empty">No lessons planned yet this term.</p>`;
    return teachers
      .map(
        (t) =>
          `<button class="card" type="button" data-act="open" data-id="${esc(t.teacher_id)}" style="text-align:left">
             <strong>${esc(t.teacher_name)}</strong>
             <div class="muted" style="margin-top:4px;font-size:var(--text-sm)">
               ${t.total} lesson${t.total === 1 ? "" : "s"} · ${t.submitted} submitted · ${t.approved} approved · ${rate(t)}
             </div>
           </button>`,
      )
      .join("");
  }

  // ── drill view (one teacher: timeline + tracking notes) ──────────────────────────
  function timelineHtml(): string {
    if (!timeline.length) return `<p class="empty">No lessons${statusFilter ? " for this filter" : ""} yet.</p>`;
    return timeline
      .map((l) => {
        const inner = `<span class="chip">W${esc(l.week_no)}</span> <strong>${esc(l.class_name)}</strong> · ${esc(l.topic)}
          <div style="margin-top:6px">${statusChip(l.status, l.on_time)}</div>`;
        return l.plan_id
          ? `<a class="card" data-link href="/plan/${esc(l.plan_id)}">${inner}</a>`
          : `<div class="card">${inner}</div>`;
      })
      .join("");
  }

  function trackingFormHtml(): string {
    const t = tracking;
    const updated = t?.updated_at ? `<p class="hint">Last updated ${esc(t.updated_at)}</p>` : "";
    return `
      <h2>Tracking notes <span class="muted">(private to you)</span></h2>
      ${updated}
      <form id="track">
        <label for="t-review">Last review</label>
        <input id="t-review" name="last_review" type="date" value="${esc(t?.last_review ?? "")}" />
        <label for="t-obs">Last observation</label>
        <input id="t-obs" name="last_observation" type="date" value="${esc(t?.last_observation ?? "")}" />
        <label for="t-focus">Current focus</label>
        <textarea id="t-focus" name="current_focus" placeholder="What this teacher is working on">${esc(t?.current_focus ?? "")}</textarea>
        <label for="t-next">Next action</label>
        <textarea id="t-next" name="next_action" placeholder="The next thing you'll do (observe, meet, follow up)">${esc(t?.next_action ?? "")}</textarea>
        <div style="height:12px"></div>
        <button class="btn" type="submit">Save notes</button>
      </form>`;
  }

  function drillHtml(): string {
    const who = teachers.find((t) => t.teacher_id === selectedId);
    const name = who?.teacher_name ?? "Teacher";
    const stat = who
      ? `<p class="hint">${who.total} lesson${who.total === 1 ? "" : "s"} · ${who.submitted} submitted · ${who.approved} approved · ${rate(who)}</p>`
      : "";
    return `
      <p><a href="#" data-act="back" class="muted">← All teachers</a></p>
      <h2>${esc(name)}</h2>
      ${stat}
      <h2>Lessons this term</h2>
      ${timelineHtml()}
      ${trackingFormHtml()}`;
  }

  function render(): void {
    const errHtml = err ? `<p class="error">${esc(err)}</p>` : "";
    const flashHtml = flash ? `<div class="toast">${esc(flash)}</div>` : "";
    app.innerHTML = `
      <div class="topbar"><a data-link href="/" class="muted">← Home</a></div>
      <h1>Lead Insights</h1>
      <p class="hint">Each teacher's work this term${term ? ` · ${esc(term.name)}` : ""}. Private to the Lead — coordination, not a scoreboard.</p>
      <div id="insroot">
        ${errHtml}${flashHtml}
        ${filterBar()}
        ${selectedId ? drillHtml() : overviewHtml()}
      </div>`;
    flash = "";

    const root = app.querySelector<HTMLElement>("#insroot")!;
    root.addEventListener("click", onClick);
    root.addEventListener("change", onChange);
    root.addEventListener("submit", onSubmit);
  }

  async function mutate(runFn: () => Promise<void>): Promise<void> {
    err = null;
    try {
      await runFn();
      await load();
    } catch (e) {
      err = (e as Error)?.message ?? "Something went wrong.";
    }
    render();
  }

  function onClick(e: Event): void {
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-act]");
    if (!el) return;
    const act = el.dataset.act;
    if (act === "open") { e.preventDefault(); selectedId = el.dataset.id ?? ""; void mutate(async () => {}); return; }
    if (act === "back") { e.preventDefault(); selectedId = ""; void mutate(async () => {}); return; }
  }

  function onChange(e: Event): void {
    const sel = e.target as HTMLSelectElement;
    if (sel.id === "f-teacher") { selectedId = sel.value; void mutate(async () => {}); return; }
    if (sel.id === "f-status") { statusFilter = sel.value; void mutate(async () => {}); return; }
  }

  function onSubmit(e: Event): void {
    const form = e.target as HTMLFormElement;
    if (form.id !== "track" || !selectedId) return;
    e.preventDefault();
    const fd = new FormData(form);
    const payload = {
      last_review: String(fd.get("last_review") ?? ""),
      last_observation: String(fd.get("last_observation") ?? ""),
      current_focus: String(fd.get("current_focus") ?? ""),
      next_action: String(fd.get("next_action") ?? ""),
    };
    void mutate(async () => {
      await api(`/api/lead/tracking/${encodeURIComponent(selectedId)}`, { method: "PUT", body: JSON.stringify(payload) });
      flash = "Notes saved.";
    });
  }

  await load();
  render();
}
